import * as React from 'react';
import { DocumentMap, IDocumentsList } from '../../../../types';
import ChecklistSection from '../../../atoms/checklist-section';
import Finger, { Status } from '../../../atoms/finger';
import { getVisible, getDocStatus } from '../../../../utilities/documents';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import { useIsSectionLocked } from '../../../../hooks/useIsSectionLocked';
import { SectionId } from '../../../molecules/Sidebar/types';

interface IProps {
  infoOnly: boolean;
  academicTranscripts: DocumentMap;
  academicCertificates: DocumentMap;
  courseName: string;
  courseYear: string;
  warning?: string;
  onResume: (url: string) => void;
  onAcademicTranscriptClick: (docKey: string, infoOnly: boolean) => void;
  onAcademicCertificateClick: (docKey: string, infoOnly: boolean) => void;
}

const PreviousStudy = (props: IProps) => {
  const { academicTranscripts, academicCertificates, courseName, courseYear, warning, infoOnly } = props;
  const offer = useSelector((state: ApplicationState) => state.student.offer);
  const isSectionLocked = useIsSectionLocked(SectionId.GENERAL_DOC, offer.status);

  let hasDocs = false;
  const docNodes: React.ReactNode[] = [];

  if (Object.keys(academicTranscripts).length > 0) {
    const visibleDocs = getVisible(academicTranscripts, infoOnly) as IDocumentsList[];
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((AT: IDocumentsList, index: number) => {
          const { key: docKey, item } = AT;
          if (item.status === 'REJECTED') {
            return (
              <Finger
                hideButton={true}
                reason={item.reason}
                key={`academic-transcript-${docKey}`}
                id={`academic-transcript-${docKey}`}
                status={Status.Rejected}
                label={`Academic transcript ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
              />
            );
          } else {
            return (
              <Finger
                key={`academic-transcript-${docKey}`}
                id={`academic-transcript-${docKey}`}
                status={getDocStatus(item.status, infoOnly, isSectionLocked)}
                completeLabel={infoOnly ? 'View' : undefined}
                resolveLabel={infoOnly ? 'Please Confirm' : undefined}
                buttonLabel={infoOnly ? 'Please review' : undefined}
                label={`Academic transcript ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
                onClick={() => props.onAcademicTranscriptClick(docKey, infoOnly)}
              />
            );
          }
        })
      );
    }
  }

  if (Object.keys(academicCertificates).length > 0) {
    const visibleDocs = getVisible(academicCertificates, infoOnly) as IDocumentsList[];
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((AC: IDocumentsList, index: number) => {
          const { key: docKey, item } = AC;
          if (item.status === 'REJECTED') {
            return (
              <Finger
                hideButton={true}
                reason={item.reason}
                key={`academic-certificate-${docKey}`}
                id={`academic-certificate-${docKey}`}
                status={Status.Rejected}
                label={`Academic certificate ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
              />
            );
          } else {
            return (
              <Finger
                key={`academic-certificate-${docKey}`}
                id={`academic-certificate-${docKey}`}
                status={getDocStatus(item.status, infoOnly, isSectionLocked)}
                completeLabel={infoOnly ? 'View' : undefined}
                resolveLabel={infoOnly ? 'Please Confirm' : undefined}
                buttonLabel={infoOnly ? 'Please review' : undefined}
                label={`Academic certificate ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
                onClick={() => props.onAcademicCertificateClick(docKey, infoOnly)}
              />
            );
          }
        })
      );
    }
  }

  if (!hasDocs) return null;

  return (
    <ChecklistSection
      label={`Upload your certificate and transcript for your ${courseName} from ${courseYear}`}
      edit={() => {
        props.onResume('history/step-1');
      }}
      warning={warning}
    >
      {docNodes}
    </ChecklistSection>
  );
};

export default PreviousStudy;
